/**
 * SkinResolver — single source of truth for game art, settings, and tuning.
 *
 * Replaces ThemeManager + themeSlots + skinDescriptor + AssetManager.
 *
 * Three public methods drive everything:
 *   skin.sprite(game, slot, opts?)  → Pixi Sprite (PNG-backed) or Graphics fallback
 *   skin.setting(game, key)         → localStorage override or config default
 *   skin.tuning(game, path)         → dotted-path read from CONFIG.games[game].tuning
 *
 * Storage:
 *   IndexedDB (via SkinStorage):
 *     - skins:    { [game]: { [slot]: { src: 'data:...' | 'assets/...' } } }
 *     - tilesets: { [game]: [{ id, name, src, gridCols, gridRows }] }
 *   localStorage (small keys, kept here for sync access at boot):
 *     - 'mini-games-settings'    → { [game]: { [key]: value } }
 *     - 'mini-games-lang'        → 'en' | 'hu'
 *     - 'mini-games-player-name' → string
 *
 * The IDB move (see SkinStorage.js) lets a designer import dozens of
 * MP3s / PNGs without blowing the legacy localStorage quota — which
 * used to silently swallow the write and lose the import on reload.
 * Construction is sync but the IDB read is async, so callers must
 * `await resolver.ready()` before relying on skin / tileset reads.
 *
 * Image cache: Map<srcPath, Texture>. Same path → same texture, no re-decode.
 */

import { SkinStorage } from './SkinStorage.js';
// Rendering half (sprite building, texture decode, atlas sub-textures,
// placeholder rects) lives in SkinSprite.js — this file keeps the
// data/config side: skins, settings, tilesets, persistence.
import {
    splitSlotKey,
    extractGame,
    buildSlotSprite,
    preloadSlotTexture,
    placeholderGraphic,
} from './SkinSprite.js';

const LS_SETTINGS = 'mini-games-settings';
const LS_LANG = 'mini-games-lang';
const LS_PLAYER = 'mini-games-player-name';

export class SkinResolver {
    /**
     * @param {object} config The merged CONFIG object (data/index.js).
     */
    constructor(config) {
        this._config = config;
        this._skins = {};
        this._settings = {};
        // Per-game imported tileset/atlas images (the "source art" a
        // designer slices in the editor's Tileset Mapper, then maps
        // individual cells onto slots — player, enemies, ground, …).
        // Shape: { [game]: [{ id, name, src, gridCols, gridRows }] }.
        this._tilesets = {};
        this._lang = 'en';
        this._lastPlayerName = '';
        this._textureCache = new Map();
        this._subTextureCache = new Map();
        this._listeners = new Set();
        // Durable backing store for skins + tilesets (IndexedDB, with
        // a one-time migration from the legacy localStorage keys).
        this._storage = new SkinStorage();
        // Latest in-flight persist for each store, so the upload
        // helpers can await the actual write and surface a clear
        // error to the user if the backend rejects it.
        this._lastSkinsPersist    = Promise.resolve(true);
        this._lastTilesetsPersist = Promise.resolve(true);
        // Kick off the async load. Callers await `ready()` before
        // reading skins / tilesets at boot.
        this._readyPromise = this._load();
    }

    /**
     * Resolves once the IDB-backed stores (skins + tilesets) and the
     * sync settings/lang/playerName have been loaded and the optional
     * preset merge has been applied. Game.init awaits this before
     * touching scenes so the first read sees the persisted state.
     */
    ready() {
        return this._readyPromise;
    }

    // ─── public reads ────────────────────────────────────────────────

    /**
     * @param {string} game e.g. 'snake'
     * @param {string} slot e.g. 'tram.head'
     * @param {object} opts { width, height, anchorX, anchorY, fitMode, scaleMultiplier, flipX }
     * @returns {Sprite | Graphics} Sprite if a PNG is set, else a colored Graphics fallback.
     */
    sprite(game, slot, opts = {}) {
        return buildSlotSprite(this, game, slot, opts);
    }

    /**
     * Returns the resolved source URI for a slot, or null.
     * @param {string} game
     * @param {string} slot
     */
    uri(game, slot) {
        return this._resolveSrc(game, slot);
    }

    /**
     * Eagerly decode the texture behind a legacy "game.slot" key into
     * the shared `_textureCache`, so every later `sprite()` /
     * `createSprite()` call for the slot applies its texture
     * SYNCHRONOUSLY (no pop-in). Used by per-stage preloaders.
     *
     * Resolves with the Texture, or null when the slot has no art
     * (procedural fallback) or the image fails — preloading is always
     * best-effort, never a hard error.
     *
     * @param {string} slotKey e.g. 'platformer.tile.ground'
     * @returns {Promise<Texture|null>}
     */
    preloadTexture(slotKey) {
        return preloadSlotTexture(this, slotKey);
    }

    /**
     * Returns the setting value (localStorage override if set, else config default).
     * @param {string} game
     * @param {string} key
     */
    setting(game, key) {
        const override = this._settings?.[game]?.[key];
        if (override !== undefined) return override;
        return this._config?.games?.[game]?.settings?.[key]?.default;
    }

    /**
     * Reads a tuning value via dotted path.
     *   skin.tuning('snake', 'smoothMotion.lerpFactor')
     * @param {string} game
     * @param {string} path
     */
    tuning(game, path) {
        const root = this._config?.games?.[game]?.tuning;
        if (!root || !path) return undefined;
        return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), root);
    }

    /**
     * Reads a shared (canvas/fonts/backButton/...) value via dotted path.
     */
    shared(path) {
        if (!this._config || !path) return undefined;
        return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), this._config);
    }

    /**
     * Returns the game's grid metadata (cellSize, cols, rows, originX, originY).
     */
    grid(game) {
        return this._config?.games?.[game]?.grid;
    }

    /**
     * Returns the game's full elements map.
     */
    elements(game) {
        return this._config?.games?.[game]?.elements;
    }

    /**
     * Returns the element descriptor for one slot (footprint, fallbackColor, ...).
     */
    element(game, slot) {
        return this._config?.games?.[game]?.elements?.[slot];
    }

    /**
     * Lists slot keys for a game in declaration order.
     */
    slots(game) {
        const elements = this._config?.games?.[game]?.elements;
        return elements ? Object.keys(elements) : [];
    }

    /**
     * Lists game ids in CONFIG order.
     */
    games() {
        return Object.keys(this._config?.games || {});
    }

    getLang() { return this._lang; }
    getLastPlayerName() { return this._lastPlayerName; }

    // ─── legacy-key shims (accept 'game.slot' strings) ───────────────
    //
    // These let the existing OptionsScene and game scenes keep working
    // without re-threading every call site through (game, slot) tuples.
    // They're thin wrappers around the canonical API above.

    /** Returns the resolved src or null. Accepts 'game.slot' or 'game.settings'. */
    get(slotKey) {
        const [game, slot] = splitSlotKey(slotKey);
        if (!game || !slot) return null;
        return this._resolveSrc(game, slot);
    }

    /** Returns the full stored descriptor (or null) for a legacy slot key. */
    getDescriptor(slotKey) {
        const [game, slot] = splitSlotKey(slotKey);
        if (!game || !slot) return null;
        const stored = this._skins?.[game]?.[slot];
        if (stored?.src) return { ...stored };
        const element = this._config?.games?.[game]?.elements?.[slot];
        return element?.defaultPath ? { src: element.defaultPath } : null;
    }

    /**
     * Returns the raw `elements[slot]` config entry from the merged config
     * (label, footprint, defaultPath, defaultPaths, fallbackColor, …).
     * Used by the Theme Studio to enumerate multi-variant slots (cars,
     * birds, bikes) and render every variation as a thumbnail.
     */
    getElement(slotKey) {
        const [game, slot] = splitSlotKey(slotKey);
        if (!game || !slot) return null;
        return this._config?.games?.[game]?.elements?.[slot] || null;
    }

    /**
     * Returns every shipped default image for a slot, in declaration order.
     * Slots with a single `defaultPath` return `[path]`; multi-variant slots
     * (`defaultPaths: [...]`) return the full array; slots with no defaults
     * return `[]`. Does not include the user's uploaded override — call
     * {@link get} for the live resolved src.
     */
    getVariations(slotKey) {
        const el = this.getElement(slotKey);
        if (!el) return [];
        if (Array.isArray(el.defaultPaths) && el.defaultPaths.length) {
            return el.defaultPaths.slice();
        }
        return el.defaultPath ? [el.defaultPath] : [];
    }

    /** Returns full skins map keyed by 'game.slot' (legacy view). */
    getAll() {
        const out = {};
        for (const [game, slots] of Object.entries(this._skins)) {
            for (const [slot, value] of Object.entries(slots)) {
                if (value?.src) out[`${game}.${slot}`] = value;
            }
        }
        return out;
    }

    /**
     * Setting read. Accepts EITHER form for the first arg:
     *   - 'snake'           (bare game id)
     *   - 'snake.settings'  (legacy slot form)
     *
     * Historically this used `splitSlotKey` which returned `[null, …]`
     * for the bare form, silently dropping the lookup and always
     * returning the fallback — that was the root cause of "I turned
     * off Leaderboards but the icon is still visible everywhere":
     * every in-game call site uses the bare `'shared'` form so the
     * read returned `true` regardless of what was persisted. Mirror
     * `setSetting`'s `extractGame` here so both forms now work
     * symmetrically with `setSetting`.
     */
    getSetting(slotKey, key, fallback = null) {
        const game = extractGame(slotKey);
        if (!game) return fallback;
        const v = this._settings?.[game]?.[key];
        if (v !== undefined) return v;
        const def = this._config?.games?.[game]?.settings?.[key]?.default;
        return def !== undefined ? def : fallback;
    }

    /**
     * Returns the schema entry for a setting, e.g.
     *   getSettingSchema('snake', 'obstacleDensity')
     *   getSettingSchema('snake.settings', 'obstacleDensity')
     * Result has shape { type, default, options?, min?, max?, step?, label? }
     * or null if not declared.
     */
    getSettingSchema(gameOrSlotKey, key) {
        const game = extractGame(gameOrSlotKey);
        if (!game || !key) return null;
        return this._config?.games?.[game]?.settings?.[key] || null;
    }

    /** Slot write with legacy key form 'game.slot'. Stores full descriptor. */
    set(slotKey, value) {
        const [game, slot] = splitSlotKey(slotKey);
        if (!game || !slot) return;
        if (!this._config?.games?.[game]?.elements?.[slot]) return;
        if (!this._skins[game]) this._skins[game] = {};
        const prevSrc = this._resolveSrc(game, slot);
        if (value === null || value === undefined) {
            delete this._skins[game][slot];
        } else if (typeof value === 'string') {
            this._skins[game][slot] = { src: value };
        } else if (typeof value === 'object' && typeof value.src === 'string') {
            this._skins[game][slot] = { ...value };
        } else {
            return;
        }
        const nextSrc = this._resolveSrc(game, slot);
        if (prevSrc && prevSrc !== nextSrc) this._invalidate(prevSrc);
        this._persistSkins();
        this._emit();
    }

    setDescriptor(slotKey, descriptor) {
        if (!descriptor) return this.set(slotKey, null);
        if (typeof descriptor === 'string') return this.set(slotKey, descriptor);
        if (typeof descriptor === 'object' && typeof descriptor.src === 'string') {
            return this.set(slotKey, descriptor);
        }
    }

    /** Clear with legacy key form. */
    clear(slotKey) {
        this.set(slotKey, null);
    }

    /** Create a sprite via legacy key form. */
    createSprite(slotKey, width, height, opts = {}) {
        const [game, slot] = splitSlotKey(slotKey);
        if (!game || !slot) return null;
        const src = this._resolveSrc(game, slot);
        if (!src) return null;
        return this.sprite(game, slot, { ...opts, width, height });
    }

    /** Returns the merged config snapshot for export (legacy). */
    exportAllData() {
        return {
            theme: this.getAll(),
            settings: this._asLegacySettings(),
            lang: this._lang,
            last_player_name: this._lastPlayerName,
        };
    }

    /** API-availability flag (now always false — no backend in v1). */
    get isApiAvailable() {
        return false;
    }

    _asLegacySettings() {
        const out = {};
        for (const [game, kv] of Object.entries(this._settings)) {
            out[`${game}.settings`] = { ...kv };
        }
        return out;
    }

    // ─── public writes ───────────────────────────────────────────────

    /**
     * Stores a skin override (data URL or path) for a slot.
     */
    setSkin(game, slot, src) {
        if (!this._config?.games?.[game]?.elements?.[slot]) return;
        if (!this._skins[game]) this._skins[game] = {};
        if (src) this._skins[game][slot] = { src };
        else delete this._skins[game][slot];
        this._invalidate(src);
        this._persistSkins();
        this._emit();
    }

    /**
     * Clears a skin override (revert to config default / fallback).
     */
    clearSkin(game, slot) {
        this.setSkin(game, slot, null);
    }

    /**
     * Updates a setting override. Accepts either 'snake' (canonical) or
     * 'snake.settings' (legacy) as the first argument.
     */
    setSetting(gameOrSlotKey, key, value) {
        const game = extractGame(gameOrSlotKey);
        if (!game || !this._config?.games?.[game]?.settings?.[key]) return;
        if (!this._settings[game]) this._settings[game] = {};
        this._settings[game][key] = value;
        this._persistSettings();
        this._emit();
    }

    setLang(lang) {
        this._lang = lang === 'hu' ? 'hu' : 'en';
        try { localStorage.setItem(LS_LANG, this._lang); } catch { /* ok */ }
        this._emit();
    }

    setLastPlayerName(name) {
        this._lastPlayerName = name || '';
        try { localStorage.setItem(LS_PLAYER, this._lastPlayerName); } catch { /* ok */ }
    }

    /**
     * Stores an uploaded file as a data URL in IndexedDB (with
     * localStorage fallback). Accepts (game, slot, file) — canonical
     * — or (slotKey, file) — legacy. Awaits the actual persist and
     * throws if the storage backend rejects the write so the Content
     * Browser surfaces a real error instead of a false "imported"
     * confirmation.
     * @returns {Promise<string>} the resulting data URL
     */
    async uploadSkin(gameOrSlotKey, slotOrFile, maybeFile) {
        let game; let slot; let file;
        if (maybeFile instanceof File || maybeFile instanceof Blob) {
            game = gameOrSlotKey;
            slot = slotOrFile;
            file = maybeFile;
        } else {
            const [g, s] = splitSlotKey(gameOrSlotKey);
            game = g; slot = s; file = slotOrFile;
        }
        if (!game || !slot || !file) throw new Error('uploadSkin: invalid args');
        const dataUrl = await this._readFileAsDataUrl(file);
        this.setSkin(game, slot, dataUrl);
        const ok = await this._lastSkinsPersist;
        if (!ok) throw new Error('Storage write failed (browser quota exceeded or storage unavailable).');
        return dataUrl;
    }

    // ─── tileset library (source atlas images) ──────────────────────
    //
    // A tileset is an imported image the designer slices into a grid in
    // the editor, then maps individual regions onto slots. Many slots
    // can share one tileset image (same `src` data URL) — the texture
    // cache dedups so it decodes once. These methods own the library;
    // the actual slot→region mapping lives in the normal skin
    // descriptors (`setDescriptor` with mappingType:'gridRegion').

    /** Returns a shallow copy of the game's tileset library (array). */
    getTilesets(game) {
        const arr = this._tilesets?.[game];
        return Array.isArray(arr) ? arr.map((t) => ({ ...t })) : [];
    }

    /** Returns one tileset entry by id, or null. */
    getTileset(game, id) {
        const arr = this._tilesets?.[game];
        if (!Array.isArray(arr)) return null;
        const found = arr.find((t) => t.id === id);
        return found ? { ...found } : null;
    }

    /**
     * Imports a tileset image file into the game's library.
     * @returns {Promise<object>} the stored tileset { id, name, src, gridCols, gridRows }
     */
    async addTilesetFromFile(game, file, meta = {}) {
        if (!game || !file) throw new Error('addTilesetFromFile: invalid args');
        const src = await this._readFileAsDataUrl(file);
        // Read the image's natural pixel size so the editor can slice
        // by tile pixel size (e.g. 16px tiles) and keep cells SQUARE,
        // matching how the game's grid cells work.
        const size = await this._readImageSize(src).catch(() => ({ w: 0, h: 0 }));
        const id = `ts_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
        const name = meta.name || (file.name ? file.name.replace(/\.[^.]+$/, '') : 'Tileset');

        // Prefer a pixel cell size when given (or default to 16px tiles,
        // the most common pixel-art size); derive cols/rows from it so
        // the grid lines fall on real tile boundaries.
        const cellSize = Math.max(1, Number(meta.cellSize) || 16);
        let gridCols = Math.max(1, Number(meta.gridCols) || 0);
        let gridRows = Math.max(1, Number(meta.gridRows) || 0);
        if (size.w && size.h && !meta.gridCols && !meta.gridRows) {
            gridCols = Math.max(1, Math.round(size.w / cellSize));
            gridRows = Math.max(1, Math.round(size.h / cellSize));
        }

        const entry = {
            id, name, src,
            imgW: size.w || 0, imgH: size.h || 0,
            cellSize,
            gridCols: gridCols || 8,
            gridRows: gridRows || 8,
        };
        if (!Array.isArray(this._tilesets[game])) this._tilesets[game] = [];
        this._tilesets[game].push(entry);
        this._persistTilesets();
        const ok = await this._lastTilesetsPersist;
        if (!ok) {
            // Roll the in-memory entry back so the editor doesn't
            // show a tileset that won't survive a reload.
            this._tilesets[game] = this._tilesets[game].filter((t) => t.id !== id);
            throw new Error('Storage write failed (browser quota exceeded or storage unavailable).');
        }
        this._emit();
        return { ...entry };
    }

    /** Patches a tileset entry (name / grid dims / cell size / image size). */
    updateTileset(game, id, patch = {}) {
        const arr = this._tilesets?.[game];
        if (!Array.isArray(arr)) return;
        const t = arr.find((x) => x.id === id);
        if (!t) return;
        if (typeof patch.name === 'string') t.name = patch.name;
        if (patch.gridCols !== undefined) t.gridCols = Math.max(1, Number(patch.gridCols) || 1);
        if (patch.gridRows !== undefined) t.gridRows = Math.max(1, Number(patch.gridRows) || 1);
        if (patch.cellSize !== undefined) t.cellSize = Math.max(1, Number(patch.cellSize) || 1);
        if (patch.imgW !== undefined) t.imgW = Math.max(0, Number(patch.imgW) || 0);
        if (patch.imgH !== undefined) t.imgH = Math.max(0, Number(patch.imgH) || 0);
        this._persistTilesets();
        this._emit();
    }

    /** Resolves the natural pixel size of an image src (data URL / path). */
    _readImageSize(src) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });
            img.onerror = () => reject(new Error('image load failed'));
            img.src = src;
        });
    }

    /** Removes a tileset from the library (slot mappings that point at
     *  its src are left intact — they keep their last-resolved art). */
    removeTileset(game, id) {
        const arr = this._tilesets?.[game];
        if (!Array.isArray(arr)) return;
        this._tilesets[game] = arr.filter((t) => t.id !== id);
        this._persistTilesets();
        this._emit();
    }

    _persistTilesets() {
        this._lastTilesetsPersist = this._storage.saveTilesets(this._tilesets).catch((err) => {
            console.warn('[SkinResolver] save tilesets failed:', err?.message || err);
            return false;
        });
        return this._lastTilesetsPersist;
    }

    /**
     * Clears every override. Settings stay in localStorage; skins and
     * tilesets are wiped from IDB (and from any legacy localStorage
     * entries SkinStorage may still know about) so a "reset all" in
     * the Theme Studio really removes them on the next reload too.
     */
    reset() {
        this._skins = {};
        this._settings = {};
        this._tilesets = {};
        try { localStorage.removeItem(LS_SETTINGS); } catch { /* ok */ }
        // Fire-and-forget — the in-memory state is already cleared so
        // the UI is correct even if the disk write takes a tick.
        this._storage.clear().catch((err) => {
            console.warn('[SkinResolver] storage clear failed:', err?.message || err);
        });
        this._textureCache.forEach((t) => t.destroy?.(true));
        this._textureCache.clear();
        this._emit();
    }

    subscribe(listener) {
        this._listeners.add(listener);
        return () => this._listeners.delete(listener);
    }

    /**
     * Snapshot for debugging / export.
     */
    snapshot() {
        return {
            skins: JSON.parse(JSON.stringify(this._skins)),
            settings: JSON.parse(JSON.stringify(this._settings)),
            lang: this._lang,
            last_player_name: this._lastPlayerName,
        };
    }

    // ─── internal ────────────────────────────────────────────────────

    _resolveSrc(game, slot) {
        const override = this._skins?.[game]?.[slot]?.src;
        if (override) return override;
        const element = this._config?.games?.[game]?.elements?.[slot];
        if (Array.isArray(element?.defaultPaths) && element.defaultPaths.length) {
            const arr = element.defaultPaths;
            return arr[Math.floor(Math.random() * arr.length)];
        }
        return element?.defaultPath || null;
    }

    _resolveDescriptor(game, slot) {
        const override = this._skins?.[game]?.[slot];
        if (override?.src) return override;
        const element = this._config?.games?.[game]?.elements?.[slot];
        if (Array.isArray(element?.defaultPaths) && element.defaultPaths.length) {
            const arr = element.defaultPaths;
            return { src: arr[Math.floor(Math.random() * arr.length)] };
        }
        return element?.defaultPath ? { src: element.defaultPath } : null;
    }

    /**
     * Build a colored Graphics rect using the slot's fallbackColor + cell footprint.
     * Useful as a last-resort visual when no PNG is configured and the caller
     * doesn't have its own placeholder. (Anchored at center.)
     */
    placeholder(game, slot, width, height) {
        return placeholderGraphic(this, game, slot, width, height);
    }

    _invalidate(src) {
        if (!src) return;
        for (const [key, texture] of this._subTextureCache.entries()) {
            if (key.startsWith(`${src}::`)) {
                texture.destroy?.(true);
                this._subTextureCache.delete(key);
            }
        }
        const cached = this._textureCache.get(src);
        if (cached) {
            cached.destroy?.(true);
            this._textureCache.delete(src);
        }
    }

    async _load() {
        const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

        // Small synchronous reads first — these always fit comfortably
        // in localStorage and are needed even if IDB is unavailable.
        try {
            const raw = localStorage.getItem(LS_SETTINGS);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (isPlainObject(parsed)) this._settings = parsed;
            }
        } catch (err) {
            console.warn('[SkinResolver] failed to parse stored settings:', err.message);
        }

        try {
            const lang = localStorage.getItem(LS_LANG);
            if (lang) this._lang = lang === 'hu' ? 'hu' : 'en';
            const name = localStorage.getItem(LS_PLAYER);
            if (name) this._lastPlayerName = name;
        } catch { /* ok */ }

        // Skins + tilesets live in IDB (with a one-time migration
        // from the legacy localStorage keys handled inside
        // SkinStorage.load).
        try {
            const { skins, tilesets } = await this._storage.load();
            if (isPlainObject(skins))    this._skins = skins;
            if (isPlainObject(tilesets)) this._tilesets = tilesets;
        } catch (err) {
            console.warn('[SkinResolver] storage load failed:', err?.message || err);
        }

        this._applyPreset(isPlainObject);
        this._emit();
    }

    /**
     * Merge an optional baked preset (window.__GAME_PRESET__, defined
     * in `data/preset.js` and loaded by `index.html`) on top of the
     * values just read from localStorage.
     *
     * Two modes:
     *   • 'soft' (default) — only fills keys the user hasn't set
     *     themselves. Returning visitors keep their tweaks.
     *   • 'hard' — preset wins on every load, per key. Use for a
     *     uniform site / kiosk experience.
     *
     * The preset's shape mirrors `exportAllData()` so a file produced
     * by the Theme Studio's "Export Preset" button can be dropped in
     * verbatim. Failures (missing global, malformed) are silent — the
     * engine still boots with the localStorage + config defaults.
     */
    _applyPreset(isPlainObject) {
        const preset = (typeof window !== 'undefined') ? window.__GAME_PRESET__ : null;
        if (!isPlainObject(preset)) return;

        const mode = preset.mode === 'hard' ? 'hard' : 'soft';
        const presetTheme    = isPlainObject(preset.theme)    ? preset.theme    : null;
        const presetSettings = isPlainObject(preset.settings) ? preset.settings : null;

        let appliedSkins = 0;
        let appliedSettings = 0;

        // Theme overrides — keys are 'game.slot' → { src }.
        if (presetTheme) {
            for (const [slotKey, value] of Object.entries(presetTheme)) {
                const dot = slotKey.indexOf('.');
                if (dot < 0) continue;
                const game = slotKey.slice(0, dot);
                const slot = slotKey.slice(dot + 1);
                if (!this._config?.games?.[game]?.elements?.[slot]) continue;
                if (!this._skins[game]) this._skins[game] = {};
                const has = this._skins[game][slot] !== undefined;
                if (mode === 'hard' || !has) {
                    if (typeof value === 'string') {
                        this._skins[game][slot] = { src: value };
                    } else if (isPlainObject(value) && typeof value.src === 'string') {
                        this._skins[game][slot] = { ...value };
                    } else {
                        continue;
                    }
                    appliedSkins++;
                }
            }
        }

        // Settings overrides — keys are 'game.settings' → { key: value }.
        if (presetSettings) {
            for (const [groupKey, kv] of Object.entries(presetSettings)) {
                if (!isPlainObject(kv)) continue;
                const dot = groupKey.indexOf('.');
                const game = dot < 0 ? groupKey : groupKey.slice(0, dot);
                if (!this._config?.games?.[game]?.settings) continue;
                if (!this._settings[game]) this._settings[game] = {};
                for (const [k, v] of Object.entries(kv)) {
                    if (!this._config.games[game].settings[k]) continue;
                    const has = this._settings[game][k] !== undefined;
                    if (mode === 'hard' || !has) {
                        this._settings[game][k] = v;
                        appliedSettings++;
                    }
                }
            }
        }

        // Optional language seed. In 'soft' mode we only apply when
        // localStorage didn't already have a language preference.
        if (typeof preset.lang === 'string') {
            const userLang = (() => {
                try { return localStorage.getItem(LS_LANG); } catch { return null; }
            })();
            if (mode === 'hard' || !userLang) {
                this._lang = preset.lang === 'hu' ? 'hu' : 'en';
            }
        }

        if (appliedSkins || appliedSettings) {
            console.log(
                `[SkinResolver] Applied ${mode} preset: ` +
                `${appliedSkins} skin(s), ${appliedSettings} setting(s)`
            );
        }
    }

    /**
     * Persist the in-memory skins map to IDB (with localStorage
     * fallback inside SkinStorage). The returned promise resolves to
     * `true` when the write actually landed — upload helpers await
     * `this._lastSkinsPersist` so they can throw a clear error when
     * the backend rejects the write (browser quota policy, private
     * mode, etc.) instead of pretending the import succeeded.
     */
    _persistSkins() {
        this._lastSkinsPersist = this._storage.saveSkins(this._skins).catch((err) => {
            console.warn('[SkinResolver] save skins failed:', err?.message || err);
            return false;
        });
        return this._lastSkinsPersist;
    }

    _persistSettings() {
        try { localStorage.setItem(LS_SETTINGS, JSON.stringify(this._settings)); }
        catch (err) { console.warn('[SkinResolver] save settings failed:', err.message); }
    }

    _readFileAsDataUrl(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }

    _emit() {
        for (const listener of this._listeners) listener(this);
    }
}
